import moment from "moment";
import * as _ from "lodash";
import { ComponentProps, FC } from "react";

import { Direction, Effect, TrackerResult } from "@ctra/api";
import { Enterprise, useTranslation } from "@ctra/i18n";
import { ImpactTracking } from "@ctra/components";

import { useAskIdaResults } from "@ask-ida";

const { KPIChange: KPIChangeComponent } = ImpactTracking;

/**
 * Change block to show % change between values
 * @param {Effect} effect
 * @param {CamelCasedPropertiesDeep<TrackerValues>} values
 * @return {JSX.Element}
 * @constructor
 */
export const KPIChange: FC<{
  effect: Effect;
  values: TrackerResult["results"][keyof TrackerResult["results"]];
}> = ({ effect, values }) => {
  const { t } = useTranslation();

  const {
    hasIncreased,
    values: { before, after, expected },
    ratioChange
  } = useAskIdaResults(values?.valueBefore, values?.valueAfter, values?.expectedAfter);

  const isTimeChange = ![Effect.milkYield, Effect.milkVisits].includes(effect);
  const beforeValue = _.round(isTimeChange ? moment.duration(before, "seconds").as("hours") : before, 2);
  const afterValue = _.round(isTimeChange ? moment.duration(after, "seconds").as("hours") : after, 2);

  const expectedValue = _.round(
    isTimeChange ? moment.duration(expected, "seconds").as("hours") : expected,
    2
  );

  const {
    askIda: {
      question: { effect: effectCopy },
      answer: {
        change: { after: afterText, before: beforeText, expected: expectedText }
      },
      createTracker: {
        configure: {
          labels: { change }
        }
      }
    },
    impactTracking: { noData }
  } = Enterprise;

  const direction = hasIncreased ? Direction.increase : Direction.decrease;

  const props: ComponentProps<typeof KPIChangeComponent> = {
    labels: {
      kpi: _.upperFirst(
        _.trim(
          t<string>(effectCopy(effect), {
            case: null,
            article: null
          })
        )
      ),
      direction: t(change(direction)),
      noData: t(noData.label)
    },
    before: {
      label: t(beforeText),
      value: beforeValue
    },
    after: {
      label: t(afterText),
      value: afterValue
    },
    expected: values?.expectedAfter
      ? {
          label: t(expectedText),
          value: expectedValue
        }
      : void 0,
    suffix: "%",
    direction,
    value: ratioChange
  };

  return <KPIChangeComponent key={effect} {...props} />;
};
